import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  PROJECT_VISIBILITIES,
  type ProjectCreateInput,
  type ProjectVisibility,
  projectCreateCall,
} from '../lib/project-create';
import { CopyAsCall } from './CopyAsCall';
import { Field } from './Field';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// The greenfield create form (#1727). Presenter — <CreateProjectDialog/> owns
// the POST and the verdict; this owns what a human types.
//
// VISIBILITY IS PICKED, NEVER DEFAULTED HERE. The field carries a default
// server-side (`private`, the safe one) and this form must not restate it: a
// second copy of a default is the drift that decides who may read a maintainer's
// new repo by which door they used. So the select opens on its placeholder and
// the submit stays shut until a human picks — the rule `<FleetRolloutModal/>`'s
// `channel` holds, for the same reason. The picker is a button and so carries no
// native `required`; the null draft below is the only thing that gates the submit.
//
// NOTHING ELSE IS VALIDATED HERE EITHER. The name's charset and every length
// bound live in `PROJECT_CREATE_FIELDS` and the server answers a 400 naming the
// field; restating them would be a second arbiter, and the one in a browser
// bundle is the one that goes stale.
//
// THE SNIPPET IS BUILT FROM THE DRAFT, and withheld until the draft is one the
// server would accept — the `<StartPlanForm/>` rule: printing the call over a
// blank name would hand the reader a command that can only 400.

export type CreateProjectFormProps = {
  /** True while the create is in flight. */
  readonly pending: boolean;
  readonly onSubmit: (input: ProjectCreateInput) => void;
  readonly onCancel: () => void;
};

export function CreateProjectForm(props: CreateProjectFormProps): JSX.Element {
  const [name, setName] = createSignal('');
  const [visibility, setVisibility] = createSignal<ProjectVisibility | ''>('');
  const [description, setDescription] = createSignal('');
  const [stack, setStack] = createSignal('');

  /**
   * The exact value the POST would carry, or NULL while the form could not make
   * that call — empties collapse to null exactly as the port folds them.
   *
   * Null rather than a filled-in default, because the alternative is where a
   * default sneaks back in: an unpicked `visibility` has no honest value here,
   * and substituting `private` to keep the type happy would put the server's
   * default in a browser bundle after all — the one thing this form's rule
   * forbids. It also made correctness depend on every caller checking `valid()`
   * first. There is no `valid()` any more: absence of a draft IS invalid, so the
   * submit, the disabled state and the snippet all read one value.
   */
  const draft = (): ProjectCreateInput | null => {
    const picked = visibility();
    const trimmed = name().trim();
    if (trimmed === '' || picked === '') return null;
    return {
      name: trimmed,
      description: description().trim() || null,
      visibility: picked,
      stack: stack().trim() || null,
    };
  };

  const submit = (e: Event): void => {
    e.preventDefault();
    const input = draft();
    if (input === null || props.pending) return;
    props.onSubmit(input);
  };

  return (
    <form onSubmit={submit}>
      <div class="field-group">
        <Field
          label={t('repos.create.name')}
          id="create-project-name"
          desc={t('repos.create.name_desc')}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              value={name()}
              onInput={(e) => setName(e.currentTarget.value)}
              placeholder={t('repos.create.name_placeholder')}
              required
            />
          )}
        </Field>

        <Field label={t('repos.create.visibility')} id="create-project-visibility">
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('repos.create.visibility')}
              block
              value={visibility()}
              options={[
                { value: '', label: t('repos.create.visibility_placeholder') },
                ...PROJECT_VISIBILITIES.map((v) => ({
                  value: v,
                  label: t(`repos.create.visibility_${v}`),
                })),
              ]}
              onChange={setVisibility}
            />
          )}
        </Field>

        <Field label={t('repos.create.description')} id="create-project-description">
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea"
              rows="2"
              value={description()}
              onInput={(e) => setDescription(e.currentTarget.value)}
            />
          )}
        </Field>

        <Field
          label={t('repos.create.stack')}
          id="create-project-stack"
          desc={t('repos.create.stack_desc')}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              value={stack()}
              onInput={(e) => setStack(e.currentTarget.value)}
              placeholder={t('repos.create.stack_placeholder')}
            />
          )}
        </Field>
      </div>

      {/* The snippet is the draft, so it renders off the same value the submit
          sends — never a parallel predicate that could disagree with it. */}
      <Show when={draft()}>
        {(input) => (
          <CopyAsCall
            calls={[{ label: t('repos.create.submit'), call: projectCreateCall(input()) }]}
          />
        )}
      </Show>

      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onCancel}>
          {t('repos.create.cancel')}
        </Button>
        <button type="submit" class={buttonClass()} disabled={draft() === null || props.pending}>
          {t(props.pending ? 'repos.create.submitting' : 'repos.create.submit')}
        </button>
      </div>
    </form>
  );
}
